import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, Spinner } from "@phosphor-icons/react";

/**
 * ContactForm · form di contatto riusabile.
 *
 * Props:
 *   - defaultSubject: chiave precompilata (es. "fattura" o "enterprise")
 *   - compact: layout più stretto per uso in modal
 *   - onSuccess: callback opzionale dopo invio andato a buon fine
 *   - hideTitle: nasconde il titolo "Scrivici" (per uso in pagine con titolo proprio)
 */
const SUBJECT_OPTIONS = [
  { key: "generale", label: "Domanda generale" },
  { key: "fattura", label: "Richiesta fattura" },
  { key: "bug", label: "Segnalazione problema tecnico" },
  { key: "disdetta", label: "Disdetta abbonamento" },
  { key: "enterprise", label: "Piano Enterprise / Studio" },
  { key: "altro", label: "Altro" },
];

export default function ContactForm({
  defaultSubject = "generale",
  compact = false,
  onSuccess,
  hideTitle = false,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;

    if (name.trim().length < 2) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Email non valida");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Scrivi almeno 10 caratteri nel messaggio");
      return;
    }

    setLoading(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject,
        message: message.trim(),
        company, // honeypot · sempre vuoto per gli umani
      });
      setSent(true);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Errore nell'invio. Riprova tra qualche minuto.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        className={`bg-[#F1F4ED] border border-[#D5DAC9] rounded-lg p-6 ${compact ? "" : "lg:p-8"} text-center`}
        data-testid="contact-form-sent"
      >
        <CheckCircle
          size={40}
          weight="duotone"
          className="text-[#7C8B6E] mx-auto mb-3"
        />
        <h3 className="font-editorial text-2xl text-[#1C1C1A]">
          Messaggio ricevuto.
        </h3>
        <p className="mt-2 text-[14px] text-[#3F3D38] leading-relaxed max-w-md mx-auto">
          Ti ricontattiamo entro 24 ore lavorative all&apos;indirizzo che hai
          indicato. Se è qualcosa di urgente, dillo nel prossimo messaggio:
          rispondiamo con priorità.
        </p>
        <button
          type="button"
          onClick={() => {
            setSent(false);
            setName("");
            setEmail("");
            setMessage("");
            setSubject(defaultSubject);
          }}
          className="mt-5 text-[13px] text-[#7C8B6E] underline underline-offset-2 hover:text-[#1C1C1A]"
          data-testid="contact-form-send-another"
        >
          Inviare un altro messaggio
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`bg-white border border-[#E5E7E0] rounded-lg ${compact ? "p-5" : "p-6 lg:p-8"} space-y-4`}
      data-testid="contact-form"
    >
      {!hideTitle && (
        <div>
          <h3 className="font-editorial text-2xl text-[#1C1C1A]">
            Scrivici.
          </h3>
          <p className="mt-1 text-[13px] text-[#6B6A65]">
            Rispondiamo entro 24 ore lavorative.
          </p>
        </div>
      )}

      <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-3`}>
        <div>
          <label className="block text-[12px] font-medium text-[#3F3D38] mb-1">
            Nome
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="contact-form-name"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-[#3F3D38] mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="contact-form-email"
            autoComplete="email"
          />
        </div>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#3F3D38] mb-1">
          Argomento
        </label>
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E] bg-white"
          data-testid="contact-form-subject"
        >
          {SUBJECT_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#3F3D38] mb-1">
          Messaggio
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          minLength={10}
          maxLength={4000}
          rows={compact ? 4 : 6}
          placeholder="Raccontaci la tua domanda con qualche dettaglio. Più è preciso, prima ti rispondiamo bene."
          className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E] resize-none"
          data-testid="contact-form-message"
        />
        <div className="mt-1 text-[11px] text-[#9A968B] text-right">
          {message.length}/4000
        </div>
      </div>

      {/* Honeypot anti-bot · hidden agli umani */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label>
          Azienda (non compilare)
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#7C8B6E] text-white rounded-md font-medium hover:bg-[#163029] transition-colors disabled:opacity-60"
        data-testid="contact-form-submit"
      >
        {loading ? (
          <>
            <Spinner size={16} className="animate-spin" />
            Invio in corso…
          </>
        ) : (
          <>
            <PaperPlaneTilt size={16} weight="fill" />
            Invia messaggio
          </>
        )}
      </button>

      <p className="text-[11px] text-[#9A968B] leading-relaxed">
        Non condividiamo la tua email con nessuno. La usiamo solo per
        rispondere a questo messaggio.
      </p>
    </form>
  );
}
